import { ReactElement } from 'react';
import { useHistory } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import {
  styled,
  Box,
  Button,
  Container,
  Text,
  Grid,
} from '@classtinginc/design-system';

import { useTranslation } from 'react-i18next';

import ErrorImage from 'assets/images/notfound-error.svg';

const StyledGrid = styled(Grid, {
  gridTemplateAreas: `
    "img img"
    "msg msg"
  `,
  '@sm': {
    gridTemplateAreas: `
      "img img img img img img"
      "msg msg msg msg msg msg"
    `,
  },
  '@lg': {
    gridTemplateAreas: `
      ". . msg msg msg msg msg img img img . ."
    `,
  },
});

const MessageBox = styled(Box, {
  gridArea: 'msg',
  display: 'grid',

  justifyItems: 'center',
  padding: '$layout-03 $00',

  '@lg': {
    padding: '$layout-09 $00',
    justifyItems: 'start',
  },

  h2: {
    fontSize: '$size-140',
    '@lg': {
      fontSize: '$size-160',
    },
  },

  p: {
    fontSize: '$size-60',
    '@lg': {
      fontSize: '$size-80',
    },
  },
});

const ImageBox = styled(Box, {
  display: 'grid',
  gridArea: 'img',
  alignContent: 'center',
  justifyContent: 'center',
  padding: '$layout-05 $00 $00 $00',
  '@lg': {
    padding: '$layout-07 $00',
  },
});

const defaultProps = {
  'data-testid': 'not-found-error-page',
};

type NotFoundErrorPageProps = typeof defaultProps;

const NotFoundErrorPage = (
  props: NotFoundErrorPageProps = defaultProps
): ReactElement => {
  const history = useHistory();
  const [t] = useTranslation();

  const handleGoBack = () => {
    history.go(-1);
  };

  return (
    <Container {...props}>
      <Helmet>
        <title>404 NotFound</title>
      </Helmet>
      <StyledGrid>
        <MessageBox>
          <Text
            as="h2"
            size="180"
            color="dark"
            css={{
              marginBottom: '$03',
            }}
          >
            {t('not_found_error_page.title')}
          </Text>
          <Text
            as="p"
            size="90"
            color="light"
            css={{
              marginBottom: '$07',
              whiteSpace: 'pre-line',
            }}
          >
            {t('not_found_error_page.description')}
          </Text>
          <Box>
            <Button
              as="a"
              href="/"
              appearance="outlined"
              color="secondary"
              size="md"
              onClick={handleGoBack}
              css={{
                marginRight: '$02',
              }}
            >
              {t('not_found_error_page.button.go_to_home')}
            </Button>
            <Button
              as="button"
              appearance="contained"
              color="primary"
              size="md"
              onClick={handleGoBack}
              data-testid="go-back"
            >
              {t('not_found_error_page.button.go_back')}
            </Button>
          </Box>
        </MessageBox>
        <ImageBox>
          <ErrorImage />
        </ImageBox>
      </StyledGrid>
    </Container>
  );
};

NotFoundErrorPage.displayName = 'NotFoundErrorPage';
NotFoundErrorPage.defaultProps = defaultProps;

export default NotFoundErrorPage;
